import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import '../styles/editable-record-table.css';
import '../styles/input-controls.css';
import LinkableTextArea from './LinkableTextArea.jsx';
import RecordDetailPopup from './RecordDetailPopup.jsx';
import { isColumnRequired } from '../utils/converters.js';

const ROW_HEIGHT = 53;
const OVERSCAN = 10;
// 本文中の埋め込みリンクをリンク化する対象。複数行のコメント欄だけを扱う
const COMMENT_COLUMN_KEYS = ['comment', 'pmoComment', 'memo', 'policyDetail', 'closeCondition'];
// 既定値を毎レンダー生成しない（新しい [] 参照が initialRows 依存の useEffect を無限発火させるのを防ぐ）
const EMPTY_ROWS = [];

function buildDefaultEmptyRow(columns) {
  return columns.reduce((acc, column) => {
    acc[column.key] = column.multiple ? [] : '';
    return acc;
  }, {});
}

export default function EditableRecordTable({
  columns,
  initialRows = EMPTY_ROWS,
  addButtonLabel = '+ レコード追加',
  createEmptyRow,
  readOnly = false,
  showAddButton = true,
  stickyColumnCount = 0,
  externalAddSignal = 0,
  onRowsChange,
  enableRowPopup = false,
  rowPopupTitle = 'レコード詳細',
  onPopupSave,
  onPopupDelete,
  highlightedRowId = null,
}) {
  const [rows, setRows] = useState(initialRows);
  const [popupRowIndex, setPopupRowIndex] = useState(null);
  const [popupDraftRow, setPopupDraftRow] = useState(null);
  const [popupSaving, setPopupSaving] = useState(false);
  const [popupDeleting, setPopupDeleting] = useState(false);
  const [popupError, setPopupError] = useState('');
  const prevExternalAddSignal = useRef(externalAddSignal);
  const isSyncingFromPropsRef = useRef(false);

  // Virtual scroll state
  const wrapRef = useRef(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(800);

  useEffect(() => {
    const el = wrapRef.current;
    /* istanbul ignore next -- ref は初回描画後に必ず存在する防御ガード */
    if (!el) return;
    const onScroll = () => setScrollTop(el.scrollTop);
    const ro = new ResizeObserver(([entry]) => {
      setViewportHeight(entry.contentRect.height);
    });
    setViewportHeight(el.clientHeight);
    el.addEventListener('scroll', onScroll, { passive: true });
    ro.observe(el);
    return () => {
      el.removeEventListener('scroll', onScroll);
      ro.disconnect();
    };
  }, []);

  useEffect(() => {
    isSyncingFromPropsRef.current = true;
    /* istanbul ignore next -- initialRows は既定引数で常に配列。null 明示渡しへの防御フォールバック */
    setRows(initialRows || []);
  }, [initialRows]);

  useEffect(() => {
    if (isSyncingFromPropsRef.current) {
      isSyncingFromPropsRef.current = false;
      return;
    }
    if (typeof onRowsChange === 'function') {
      onRowsChange(rows);
    }
  }, [onRowsChange, rows]);

  const visibleColumns = useMemo(
    () => columns.filter((c) => !c.hidden),
    [columns],
  );

  const gridTemplateColumns = useMemo(
    () => visibleColumns.map((column) => column.width || '160px').join(' '),
    [visibleColumns],
  );

  const fixedColumnCount = Math.max(0, Math.min(stickyColumnCount, visibleColumns.length));

  const stickyLeftOffsets = useMemo(() => {
    const widths = visibleColumns.map((column) => {
      const width = column.width || '160px';
      const match = width.match(/([\d.]+)px/);
      return match ? Number(match[1]) : 160;
    });

    let running = 0;
    return widths.map((w) => {
      const current = running;
      running += w;
      return current;
    });
  }, [visibleColumns]);

  const updateCell = (index, column, value) => {
    setRows((prev) => prev.map((row, i) => {
      if (i !== index) return row;

      const nextRow = { ...row, [column.key]: value };
      if (typeof column.onChange === 'function') {
        return column.onChange({
          row,
          nextRow,
          value,
          rows: prev,
        });
      }

      return nextRow;
    }));
  };

  const handleAddRow = () => {
    const row = createEmptyRow ? createEmptyRow() : buildDefaultEmptyRow(columns);
    setRows((prev) => [...prev, row]);
    requestAnimationFrame(() => {
      const el = wrapRef.current;
      if (el) el.scrollTop = el.scrollHeight;
    });
  };

  useEffect(() => {
    if (readOnly) return;
    if (externalAddSignal === prevExternalAddSignal.current) return;

    prevExternalAddSignal.current = externalAddSignal;
    handleAddRow();
  }, [externalAddSignal, readOnly]);

  const openPopup = (index) => {
    setPopupRowIndex(index);
    setPopupDraftRow({ ...rows[index] });
  };

  const closePopup = () => {
    setPopupRowIndex(null);
    setPopupDraftRow(null);
    setPopupError('');
  };

  const updateDraftCell = (column, value) => {
    setPopupDraftRow((prev) => {
      /* istanbul ignore next -- ポップアップ表示中のみ呼ばれ prev は常に存在する防御ガード */
      if (!prev) return prev;
      const nextRow = { ...prev, [column.key]: value };
      if (typeof column.onChange === 'function') {
        return column.onChange({
          row: prev,
          nextRow,
          value,
          rows,
        });
      }
      return nextRow;
    });
  };

  const handlePopupSave = async () => {
    /* istanbul ignore next -- 呼び出し元は全て onPopupSave と選択行を渡す防御ガード */
    if (!onPopupSave || popupRowIndex === null || !popupDraftRow) return;

    // セレクトの値が選択肢に含まれていない場合はクリア（表示と状態の不一致防止）
    const draft = { ...popupDraftRow };
    let draftNormalized = false;
    columns.forEach((c) => {
      if (c.type === 'select' && !c.multiple) {
        const val = draft[c.key];
        if (val) {
          const opts = getSelectOptions(c, draft);
          if (!opts.includes(val)) {
            draft[c.key] = '';
            draftNormalized = true;
          }
        }
      }
    });
    if (draftNormalized) {
      setPopupDraftRow(draft);
    }

    // 必須項目チェック
    const missingFields = columns
      .filter((c) => isColumnRequired(c, draft) && !c.readOnly)
      .filter((c) => {
        const val = draft[c.key];
        if (Array.isArray(val)) return val.length === 0;
        if (typeof val === 'boolean') return false;
        return !val && val !== 0;
      })
      .map((c) => c.label);

    if (missingFields.length > 0) {
      setPopupError(`以下の必須項目が未入力です：\n${missingFields.join('、')}`);
      return;
    }

    const idx = popupRowIndex;
    const originalRow = rows[idx];
    try {
      setPopupSaving(true);
      setPopupError('');
      // ドラフトを rows に反映
      setRows((prev) => prev.map((r, i) => (i === idx ? draft : r)));
      await onPopupSave(draft);
      setPopupRowIndex(null);
      setPopupDraftRow(null);
    } catch (e) {
      // 保存失敗時は元に戻す
      setRows((prev) => prev.map((r, i) => (i === idx ? originalRow : r)));
      setPopupError(e.message || '保存に失敗しました');
    } finally {
      setPopupSaving(false);
    }
  };

  const handlePopupDelete = async (row) => {
    /* istanbul ignore next -- 呼び出し元は全て onPopupDelete と対象行を渡す防御ガード */
    if (!onPopupDelete || !row) return;
    try {
      setPopupDeleting(true);
      setPopupError('');
      await onPopupDelete(row);
      setRows((prev) => prev.filter((r) => r.id !== row.id));
      setPopupRowIndex(null);
      setPopupDraftRow(null);
    } catch (e) {
      setPopupError(e.message || '削除に失敗しました');
    } finally {
      setPopupDeleting(false);
    }
  };

  const getSelectOptions = (column, row) => {
    if (typeof column.getOptions === 'function') {
      return column.getOptions({ row, rows }) || [''];
    }
    return column.options || [''];
  };

  const getMultiSelectValue = (rowValue) => {
    if (Array.isArray(rowValue)) return rowValue;
    if (!rowValue) return [];
    return String(rowValue)
      .split(',')
      .map((v) => v.trim())
      .filter(Boolean);
  };

  const formatPopupValue = (column, value, row) => {
    // カスタム表示（成果物セットなど）は readOnly ポップアップでも renderDisplay を使う
    if (column.renderDisplay) {
      return column.renderDisplay(value, row, { readOnly: true });
    }
    if (column.type === 'checkbox') {
      return value ? 'はい' : 'いいえ';
    }
    return value || '—';
  };

  const renderCellEditor = (row, index, column, forceEditable = false, onCellUpdate = null) => {
    const cellUpdate = onCellUpdate || ((col, val) => updateCell(index, col, val));

    // カスタムエディタ（成果物セットなど）はポップアップ内で専用ウィジェットを描画する
    if (column.renderEditor && forceEditable) {
      return column.renderEditor({
        row,
        value: row[column.key],
        update: (val) => cellUpdate(column, val),
        // 1つの操作で複数項目を変えたいエディタ向け
        // （例: 「成果物なし」ボタンは成果物名とフラグを同時に切り替える）
        updateRow: (patch) => Object.entries(patch).forEach(([key, val]) => {
          cellUpdate(columns.find((c) => c.key === key) || { key }, val);
        }),
      });
    }
    if (column.type === 'tags') {
      const raw = row[column.key] || '';
      const filled = raw.split(',').map((s) => s.trim()).filter(Boolean);
      const hasNewSlot = raw.trimEnd().endsWith(',');
      const items = hasNewSlot ? [...filled, ''] : filled;

      // ポップアップ内ではセレクトボックス表示
      if (forceEditable) {
        let opts;
        if (typeof column.getOptions === 'function') {
          opts = column.getOptions({ row, rows });
        } else if (column.options) {
          opts = column.options;
        } else {
          const allValues = new Set();
          rows.forEach((r) => {
            const v = r[column.key] || '';
            v.split(',').map((s) => s.trim()).filter(Boolean).forEach((s) => allValues.add(s));
          });
          opts = ['', ...Array.from(allValues)];
        }

        const addItem = () => {
          const current = row[column.key] || '';
          const trimmed = current.replace(/,\s*$/, '');
          cellUpdate(column, trimmed ? trimmed + ',' : ',');
        };

        if (items.length === 0) {
          return (
            <button
              type="button"
              onClick={addItem}
              style={{
                border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
                padding: '4px 12px', fontSize: '13px', color: '#6b7280', cursor: 'pointer',
              }}
            >
              ＋ 追加
            </button>
          );
        }

        return (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
            {items.map((item, i) => {
              const selectOpts = opts.includes(item) ? opts : [...opts, item];
              return (
                <div key={`${column.key}-${i}`} style={{ display: 'flex', gap: '4px', alignItems: 'center' }}>
                  <select
                    className="db-select-control"
                    value={item}
                    onChange={(e) => {
                      const newItems = [...items];
                      newItems[i] = e.target.value;
                      cellUpdate(column, newItems.filter(Boolean).join(', '));
                    }}
                    style={{ flex: 1 }}
                  >
                    {selectOpts.map((opt) => (
                      <option key={opt || 'empty'} value={opt}>
                        {opt || '選択してください'}
                      </option>
                    ))}
                  </select>
                  <button
                    type="button"
                    onClick={() => {
                      const newItems = items.filter((_, idx) => idx !== i);
                      cellUpdate(column, newItems.join(', '));
                    }}
                    style={{
                      border: 'none', background: 'transparent', color: '#ef4444',
                      fontSize: '16px', cursor: 'pointer', padding: '2px 6px', lineHeight: 1,
                    }}
                    title="削除"
                  >
                    ×
                  </button>
                </div>
              );
            })}
            <button
              type="button"
              onClick={addItem}
              style={{
                border: '1px dashed #d1d5db', background: '#fff', borderRadius: '6px',
                padding: '3px 10px', fontSize: '12px', color: '#6b7280', cursor: 'pointer',
                alignSelf: 'flex-start',
              }}
            >
              ＋ 追加
            </button>
          </div>
        );
      }

      // テーブルセルでは省略表示
      if (filled.length === 0) return <span style={{ color: '#9ca3af' }}>—</span>;
      return (
        <span style={{ fontSize: '13px', color: '#101828' }}>
          {filled[0]}
          {filled.length > 1 && (
            <span style={{ color: '#6b7280', marginLeft: '4px', fontSize: '11px' }}>
              他{filled.length - 1}件
            </span>
          )}
        </span>
      );
    }

    if (readOnly || (enableRowPopup && !forceEditable)) {
      if (column.renderDisplay) {
        return column.renderDisplay(row[column.key], row);
      }
      if (column.type === 'checkbox') {
        return <span>{row[column.key] ? '\u2713' : ''}</span>;
      }
      if (column.type === 'url' && row[column.key]) {
        return (
          <a
            href={row[column.key]}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            style={{ color: '#2563eb', textDecoration: 'underline', wordBreak: 'break-all' }}
          >
            {row[column.key]}
          </a>
        );
      }
      // コメント系は本文に埋め込みリンクのURLが入るため、一覧セルでもリンク化する
      if (COMMENT_COLUMN_KEYS.includes(column.key)) {
        return <LinkableTextArea readOnly value={row[column.key]} />;
      }
      return <span style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>{row[column.key] || ''}</span>;
    }

    if (column.type === 'select') {
      return (
        <select
          className="db-select-control"
          multiple={Boolean(column.multiple)}
          onChange={(e) => {
            if (column.multiple) {
              const selected = Array.from(e.target.selectedOptions).map((opt) => opt.value);
              cellUpdate(column, selected.join(', '));
              return;
            }
            cellUpdate(column, e.target.value);
          }}
          value={column.multiple ? getMultiSelectValue(row[column.key]) : (row[column.key] || '')}
        >
          {getSelectOptions(column, row).map((option) => (
            <option key={option || 'empty'} value={option}>
              {option || '選択してください'}
            </option>
          ))}
        </select>
      );
    }

    if (column.type === 'checkbox') {
      // getDisabled を持つ列は、行の内容に応じてチェック不可にできる
      // （例: 成果物管理の領域チェックは、該当する進捗管理が無い領域を非活性にする）
      const disabled = column.getDisabled ? Boolean(column.getDisabled({ row })) : false;
      return (
        <input
          checked={Boolean(row[column.key])}
          disabled={disabled}
          onChange={(e) => cellUpdate(column, e.target.checked)}
          title={disabled ? column.disabledHint : undefined}
          type="checkbox"
        />
      );
    }

    if (COMMENT_COLUMN_KEYS.includes(column.key)) {
      return (
        <LinkableTextArea
          onChange={(next) => cellUpdate(column, next)}
          value={row[column.key]}
        />
      );
    }

    if (column.type === 'date') {
      return (
        <input
          className="db-input-control"
          max="9999-12-31"
          onChange={(e) => cellUpdate(column, e.target.value)}
          type="date"
          value={row[column.key] || ''}
        />
      );
    }

    return (
      <input
        className="db-input-control"
        onChange={(e) => cellUpdate(column, e.target.value)}
        type="text"
        value={row[column.key] || ''}
      />
    );
  };

  const renderPopupEditor = (row, column) => {
    /* istanbul ignore next -- ポップアップ表示中のみ描画される防御ガード */
    if (popupRowIndex === null || !popupDraftRow) return null;
    return renderCellEditor(popupDraftRow, popupRowIndex, column, true, updateDraftCell);
  };

  // ハイライト行へ自動スクロール
  useEffect(() => {
    if (highlightedRowId == null) return;
    const rowIndex = rows.findIndex((r) => r.id === highlightedRowId);
    if (rowIndex < 0) return;
    const el = wrapRef.current;
    /* istanbul ignore next -- ハイライト発火時に wrapRef は必ず存在する防御ガード */
    if (!el) return;
    requestAnimationFrame(() => {
      const targetTop = rowIndex * ROW_HEIGHT;
      const targetBottom = targetTop + ROW_HEIGHT;
      // 対象行が表示範囲外の場合のみスクロール
      if (targetTop < el.scrollTop) {
        el.scrollTop = targetTop;
      } else if (targetBottom > el.scrollTop + el.clientHeight) {
        el.scrollTop = targetBottom - el.clientHeight;
      }
    });
  }, [highlightedRowId, rows]);

  // Virtual scroll calculation
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(rows.length, Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN);
  const topPad = startIndex * ROW_HEIGHT;
  const bottomPad = Math.max(0, (rows.length - endIndex) * ROW_HEIGHT);

  return (
    <div className="db-entry-table">
      <div className="db-entry-toolbar">
        {!readOnly && showAddButton && (
          <button className="db-entry-add-button" onClick={handleAddRow} type="button">
            {addButtonLabel}
          </button>
        )}
      </div>

      <div className="db-entry-wrap" ref={wrapRef}>
        <div className="db-entry-inner">
          <div className="db-entry-row db-entry-head" style={{ gridTemplateColumns }}>
            {visibleColumns.map((column, columnIndex) => (
              <div
                className={`db-entry-cell ${columnIndex < fixedColumnCount ? 'db-entry-cell--sticky' : ''}`.trim()}
                key={column.key}
                style={
                  columnIndex < fixedColumnCount
                    ? { '--sticky-left': `${stickyLeftOffsets[columnIndex]}px` }
                    : undefined
                }
              >
                {column.label}
              </div>
            ))}
          </div>

          <div className="db-entry-body" style={{ paddingTop: topPad, paddingBottom: bottomPad }}>
            {rows.slice(startIndex, endIndex).map((row, localIndex) => {
              const index = startIndex + localIndex;
              return (
              <div
                className={`db-entry-row${enableRowPopup ? ' db-entry-row--clickable' : ''}${highlightedRowId != null && row.id === highlightedRowId ? ' db-entry-row--highlighted' : ''}`}
                key={`${index}-${row.id || row.name || 'row'}`}
                style={{ gridTemplateColumns, height: ROW_HEIGHT, cursor: enableRowPopup ? 'pointer' : undefined }}
                onClick={enableRowPopup ? () => openPopup(index) : undefined}
              >
                {visibleColumns.map((column, columnIndex) => (
                  <div
                    className={`db-entry-cell ${columnIndex < fixedColumnCount ? 'db-entry-cell--sticky' : ''}`.trim()}
                    key={`${column.key}-${index}`}
                    style={
                      columnIndex < fixedColumnCount
                        ? { '--sticky-left': `${stickyLeftOffsets[columnIndex]}px` }
                        : undefined
                    }
                  >
                    {renderCellEditor(row, index, column)}
                  </div>
                ))}
              </div>
              );
            })}
          </div>
        </div>
      </div>

      <RecordDetailPopup
        columns={columns}
        formatValue={formatPopupValue}
        onClose={closePopup}
        onDelete={!readOnly && onPopupDelete ? handlePopupDelete : undefined}
        onSave={!readOnly && onPopupSave ? handlePopupSave : undefined}
        open={Boolean(popupDraftRow)}
        readOnly={readOnly}
        renderField={renderPopupEditor}
        row={popupDraftRow}
        deleting={popupDeleting}
        saving={popupSaving}
        title={rowPopupTitle}
      />

      {popupError && (
        <div className="db-error-modal-overlay" onClick={() => setPopupError('')} role="presentation">
          <div className="db-error-modal" onClick={(e) => e.stopPropagation()} role="alertdialog" aria-modal="true">
            <div className="db-error-modal-icon">!</div>
            <p className="db-error-modal-message">{popupError}</p>
            <button className="db-error-modal-button" onClick={() => setPopupError('')} type="button">
              OK
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
